import lodash from 'lodash';
import { binderInstitutionIdSelector } from 'common/selectors';
import { queryGraphQL, mutateGraphQL } from 'complionUtil/query';

import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { toMutation, toQuery, nodeWithArgs, node } from 'complionUtil/queryHelper';

import { loadStudySignaturesV2, loadSignatureExpectationsV2 } from './studySignaturesV2';

import { retrieveStudyAuditLogData } from './studyAuditLogV2';

export const LOAD_STUDY_DELEGATIONS_V2 = 'LOAD_STUDY_DELEGATIONS_V2';
export const ADD_STUDY_DELEGATION_V2 = 'ADD_STUDY_DELEGATION_V2';
export const DELETE_STUDY_DELEGATION_V2 = 'DELETE_STUDY_DELEGATION_V2';
export const UPDATE_STUDY_DELEGATION_V2 = 'UPDATE_STUDY_DELEGATION_V2';
export const UNLOAD_STUDY_TASKS_AND_DELEGATIONS_V2 = 'UNLOAD_STUDY_TASKS_AND_DELEGATIONS_V2';

export function loadStudyDelegationsV2(binderId, studyId) {
    return async (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        dispatch({
            type: 'LOADING_DELEGATIONS',
            payload: null,
        });
        const query = nodeWithArgs(
            'studyDelegationsV2',
            {
                binderId,
                institutionId,
                studyId,
            },
            [
                node('user', ['id', 'displayName', 'emailAddress']),
                node('rolesManagement', ['id', 'name']),
                'id',
                'taskIds',
                'startDate',
                'endDate',
                'createdAt',
            ]
        );

        return queryGraphQL(toQuery(query)).then((results) => {
            const { studyDelegationsV2 } = results.data;

            dispatch({
                type: LOAD_STUDY_DELEGATIONS_V2,
                payload: studyDelegationsV2,
            });
            dispatch(loadStudySignaturesV2(binderId, studyId));
        });
    };
}

export const buildAddStudyDelegationV2Query = ({ delegations, institutionId, studyId }) => {
    return nodeWithArgs(
        'createStudyDelegationV2',
        {
            institutionId,
            studyId,
            delegations,
        },
        [
            'id',
            node('user', ['id', 'displayName', 'emailAddress']),
            node('rolesManagement', ['id', 'name']),
            'taskIds',
            'startDate',
            'endDate',
        ]
    );
};

export function createStudyDelegationForStudy(
    institutionId,
    binderId,
    delegation,
    signatureExpectations,
    defaultSignatureExpectations,
    onSuccess,
    studyId
) {
    return (dispatch, getState) => {
        const state = getState();
        const _institutionId = institutionId ? institutionId : binderInstitutionIdSelector(state);
        
        if(!studyId) {
            dispatch(addStudyDelegationV2(binderId, delegation, signatureExpectations, defaultSignatureExpectations, onSuccess));
            return;
        }
        
        dispatch({
            type: 'UPSERT_DELEGATION_BEGIN',
        }); 

        const query = nodeWithArgs(
            'createStudyFromStudyDelegationV2',
            {
                institutionId: _institutionId,
                studyId,
                delegation: {
                    binderId,
                    studyId,
                    ...delegation,
                },
                signatureExpectations,
                defaultSignatureExpectations
            },
            [
                'id',
                node('user', ['id', 'displayName', 'emailAddress']),
                node('rolesManagement', ['id', 'name']),
                'taskIds',
                'startDate',
                'endDate',
            ]
        );

        return mutateGraphQL(toMutation(query)).then((results) => {
            dispatch({
                type: 'UPSERT_DELEGATION_END',
            });

            if (results && results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to create delegation'));
            } else {
                dispatch({
                    type: UPDATE_STUDY_DELEGATION_V2,
                    payload: results.data.updateStudyDelegationV2,
                })
                    .then(dispatch(loadStudyDelegationsV2(binderId, studyId)))
                    .then(dispatch(loadSignatureExpectationsV2(binderId, studyId)))
                    .then(dispatch(retrieveStudyAuditLogData(binderId, studyId)))
                    .then(() => onSuccess && onSuccess());
            }
        });
    };
}

export function addStudyDelegationV2(binderId, delegation, signatureExpectations, defaultSignatureExpectations, onSuccess, studyId) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);
        dispatch({
            type: 'UPSERT_DELEGATION_BEGIN',
        });
        const delegations = [{
            delegation: {
                binderId,
                studyId,
                ...delegation,
            },
            signatureExpectations,
            defaultSignatureExpectations
        }];
        const query = buildAddStudyDelegationV2Query({ delegations, institutionId, studyId });
        return mutateGraphQL(toMutation(query)).then((results) => {
            dispatch({
                type: 'UPSERT_DELEGATION_END',
            });
            if (results && results.errors) {
                let errorObj = lodash.first(results.errors);
                if (errorObj && errorObj.message) {
                    dispatch(addApplicationMessage('error', errorObj.message));
                } else {
                    dispatch(addApplicationMessage('error', 'Failed to add user delegations'));
                }
            } else {
                dispatch({
                    type: ADD_STUDY_DELEGATION_V2,
                    payload: results.data.createStudyDelegationV2,
                })
                    .then(dispatch(loadSignatureExpectationsV2(binderId, studyId)))
                    .then(dispatch(loadStudyDelegationsV2(binderId, studyId)))
                    .then(dispatch(retrieveStudyAuditLogData(binderId, studyId)))
                    .then(() => onSuccess && onSuccess());
            }
        });
    };
}


export function deleteStudyDelegationV2(delegationId, binderId, studyId, shouldLoadDOA = true) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);

        const query = nodeWithArgs(
            'deleteStudyDelegationV2',
            {
                id: delegationId,
                binderId,
                studyId,
                institutionId
            },
            [
                'id'
            ]
        );

        return mutateGraphQL(toMutation(query)).then((results) => {
            if (results && results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to delete delegation'));

            } else {
                if (shouldLoadDOA) {
                    dispatch({
                        type: DELETE_STUDY_DELEGATION_V2,
                        payload: results.data,
                    })
                        .then(dispatch(loadSignatureExpectationsV2(binderId, studyId)))
                        .then(dispatch(loadStudyDelegationsV2(binderId, studyId)))
                        .then(dispatch(retrieveStudyAuditLogData(binderId, studyId)));
                }
                else {
                    dispatch({
                        type: DELETE_STUDY_DELEGATION_V2,
                        payload: results.data,
                    });
                }
            }
        });
    };
}


export function updateStudyDelegationV2(binderId, delegation, signatureExpectations, defaultSignatureExpectations, onSuccess, studyId) {
    return (dispatch, getState) => {
        const state = getState();
        const institutionId = binderInstitutionIdSelector(state);

        dispatch({
            type: 'UPSERT_DELEGATION_BEGIN',
        });

        const query = nodeWithArgs(
            'updateStudyDelegationV2',
            {
                institutionId,
                studyId,
                delegation: {
                    binderId,
                    studyId,
                    ...delegation,
                },
                signatureExpectations,
                defaultSignatureExpectations
            },
            [
                'id',
                node('user', ['id', 'displayName', 'emailAddress']),
                node('rolesManagement', ['id', 'name']),
                'taskIds',
                'startDate',
                'endDate',
            ]
        );

        return mutateGraphQL(toMutation(query)).then((results) => {
            dispatch({
                type: 'UPSERT_DELEGATION_END',
            });

            if (results && results.errors) {
                const errors = lodash.get(results, 'errors');
                const message = lodash.get(errors[0], 'message', '');
                const [statusCode] = message.split(' - ');
                if (statusCode === '404') {
                    dispatch(addApplicationMessage('error', 'This Study Team Member has already been removed from the Study.'));
                    window.location.reload();
                }
                else {
                    dispatch(addApplicationMessage('error', 'Failed to update delegation'));
                }
            } else {
                dispatch({
                    type: UPDATE_STUDY_DELEGATION_V2,
                    payload: results.data.updateStudyDelegationV2,
                })
                    .then(dispatch(loadStudyDelegationsV2(binderId, studyId)))
                    .then(dispatch(loadSignatureExpectationsV2(binderId, studyId)))
                    .then(dispatch(retrieveStudyAuditLogData(binderId, studyId)))
                    .then(() => onSuccess && onSuccess());
            }
        });
    };
}

export function unloadStudyTasksAndDelegationsV2() {
    return {
        type: UNLOAD_STUDY_TASKS_AND_DELEGATIONS_V2,
    };
}

export function stackAnErrorMessage(message) {
    return (dispatch, _getState) => {
        dispatch(addApplicationMessage('error', message));
    };
}

export function stackASuccessMessage(message) {
    return (dispatch) => {
        dispatch(addApplicationMessage('success', message));
    };
}
